// components/admin/AdminChats.jsx
import { useState, useEffect } from 'react';
import axios from 'axios';
import { MessageSquare, CheckCircle, Clock, Search, RefreshCw } from 'lucide-react';
import AdminChatView from './AdminChatView';
import { socket } from '../chat/socket';

const API_URL = process.env.REACT_APP_API_URL;

function AdminChats({ userId }) {
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingChats, setPendingChats] = useState([]);
  const [completedChats, setCompletedChats] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    loadChats();
    
    // Join admin chat list room for real-time updates
    console.log('[AdminChats] Joining admin_chat_list room');
    socket.emit('join_admin_chat_list');
    
    // Listen for new chat sessions
    const handleNewChatSession = (data) => {
      console.log('[AdminChats] New chat session received:', data);
      loadChats(); // Refresh the list
    };
    
    // Listen for completed chats
    const handleChatCompleted = (data) => {
      console.log('[AdminChats] Chat completed:', data);
      loadChats(); // Refresh the list
    };
    
    // Listen for new messages (to update last message preview)
    const handleNewMessage = (data) => {
      console.log('[AdminChats] New message in session:', data.session_id);
      // Optionally update the specific chat in the list without full reload
      updateChatPreview(data.session_id, data.message);
    };
    
    socket.on('new_chat_session', handleNewChatSession);
    socket.on('chat_completed', handleChatCompleted);
    socket.on('new_message', handleNewMessage);
    
    // Cleanup
    return () => {
      socket.emit('leave_admin_chat_list');
      socket.off('new_chat_session', handleNewChatSession);
      socket.off('chat_completed', handleChatCompleted);
      socket.off('new_message', handleNewMessage);
      console.log('[AdminChats] Left admin_chat_list room');
    };
  }, [userId]);

  const loadChats = async () => {
    try {
      setIsLoading(true);
      
      const [pendingRes, completedRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/chats/pending?user_id=${userId}`),
        axios.get(`${API_URL}/api/admin/chats/completed?user_id=${userId}`)
      ]);
      
      setPendingChats(pendingRes.data.chats);
      setCompletedChats(completedRes.data.chats);
    } catch (error) {
      console.error('Error loading chats:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadChats();
  };

  const updateChatPreview = (sessionId, message) => {
    // Update the last message for a specific chat without full reload
    const updateList = (chats) => {
      return chats.map(chat => {
        if (chat.session_id === sessionId) {
          return {
            ...chat,
            last_message: {
              content: message.content,
              timestamp: message.timestamp,
              sender: message.sender
            },
            updated_at: message.timestamp
          };
        }
        return chat;
      });
    };

    setPendingChats(prev => updateList(prev));
    setCompletedChats(prev => updateList(prev));
  };

  const handleChatSelect = (session) => {
    setSelectedSession(session);
  };

  const handleBackToList = () => {
    setSelectedSession(null);
    loadChats(); // Refresh the list when coming back
  };

  const filterChats = (chats) => {
    if (!searchTerm) return chats;
    
    return chats.filter(chat => 
      chat.first_message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      chat.seller?.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      chat.buyer_details?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  if (selectedSession) {
    return (
      <AdminChatView
        sessionId={selectedSession.session_id}
        userId={userId}
        onBack={handleBackToList}
      />
    );
  }

  const currentChats = activeTab === 'pending' ? pendingChats : completedChats;
  const filteredChats = filterChats(currentChats);

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-montserrat font-bold text-gray-800">
                Chat Management
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Gestiona las conversaciones con los clientes
              </p>
            </div>
            
            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">
                {isRefreshing ? 'Actualizando...' : 'Actualizar'}
              </span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-t">
          <button
            onClick={() => setActiveTab('pending')}
            className={`flex-1 px-6 py-3 font-semibold transition-colors ${
              activeTab === 'pending'
                ? 'text-brand-primary border-b-2 border-brand-primary bg-brand-primary/5'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Clock className="w-5 h-5" />
              <span>Pendientes</span>
              <span className="bg-brand-primary text-white text-xs px-2 py-1 rounded-full">
                {pendingChats.length}
              </span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`flex-1 px-6 py-3 font-semibold transition-colors ${
              activeTab === 'completed'
                ? 'text-brand-green border-b-2 border-brand-green bg-brand-green/5'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <CheckCircle className="w-5 h-5" />
              <span>Completados</span>
              <span className="bg-brand-green text-white text-xs px-2 py-1 rounded-full">
                {completedChats.length}
              </span>
            </div>
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-t">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar chats..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 text-gray-400 animate-pulse mx-auto mb-4" />
              <p className="text-gray-600">Cargando chats...</p>
            </div>
          </div>
        ) : filteredChats.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">
                {searchTerm ? 'No se encontraron chats' : 'No hay chats todavía'}
              </p>
              <button
                onClick={handleRefresh}
                className="mt-4 text-brand-primary hover:underline"
              >
                Actualizar lista
              </button>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredChats.map((chat) => (
              <ChatListItem
                key={chat.session_id}
                chat={chat}
                isCompleted={activeTab === 'completed'}
                onClick={() => handleChatSelect(chat)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ChatListItem({ chat, isCompleted, onClick }) {
  const getPreviewText = () => {
    if (chat.last_message?.content) {
      return chat.last_message.content.substring(0, 100);
    }
    return chat.first_message?.substring(0, 100) || 'Sin mensajes';
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (hours < 1) return 'Hace unos minutos';
    if (hours < 24) return `Hace ${hours}h`;
    return date.toLocaleDateString('es-PE', { month: 'short', day: 'numeric' });
  };

  return (
    <div
      onClick={onClick}
      className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-full ${isCompleted ? 'bg-brand-green/10' : 'bg-brand-primary/10'}`}>
          <MessageSquare className={`w-6 h-6 ${isCompleted ? 'text-brand-green' : 'text-brand-primary'}`} />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div>
              <h3 className="font-semibold text-gray-800">
                {isCompleted ? chat.buyer_details?.name : 'Cliente Anónimo'}
              </h3>
              <p className="text-sm text-gray-600">
                Tienda: {chat.seller?.username || 'Desconocida'}
              </p>
            </div>
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {formatTimestamp(chat.updated_at)}
            </span>
          </div>
          
          {isCompleted && chat.buyer_details && (
            <div className="text-xs text-gray-600 mb-1">
              📱 {chat.buyer_details.phone}
            </div>
          )}
          
          <p className="text-sm text-gray-600 line-clamp-2">
            {getPreviewText()}
          </p>
          
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-gray-500">
              {chat.message_count} mensajes
            </span>
            {isCompleted && (
              <span className="text-xs bg-brand-green/10 text-brand-green px-2 py-1 rounded-full">
                Completado
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminChats;